// BrandCard.js - Updated to always redirect to single slug page
import React from 'react';
import { Star, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BrandImage } from './OptimizedImage';

// Star Rating Component
const StarRating = ({ rating }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  
  return (
    <div className="flex items-center">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={16}
          className={`${
            i < fullStars
              ? "text-yellow-400 fill-yellow-400"
              : i === fullStars && hasHalfStar
              ? "text-yellow-400 fill-yellow-400"
              : "text-gray-300"
          }`}
        />
      ))}
      <span className="ml-1 text-xs font-semibold text-white">{rating}</span>
    </div>
  );
};

// Brand Card Component
function BrandCard({ brand, priority = false, isLCPCandidate = false }) {
  const navigate = useNavigate();
  
  // Generate slug if it doesn't exist (backward compatibility)
  const slug = brand.slug || brand.id || 'brand';
  
  // Handle card click to navigate to brand details page
  const handleCardClick = (event) => {
    // Check if command key (Mac) or ctrl key (Windows/Linux) is pressed
    if (event.metaKey || event.ctrlKey) {
      // Open in new tab
      window.open(`/brand/${slug}`, '_blank');
    } else {
      // Normal navigation
      navigate(`/brand/${slug}`);
    }
  };
  
  return (
    <div 
      className={`bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 transition-all hover:shadow-xl h-full flex flex-col cursor-pointer ${
        priority ? 'brand-card-priority' : ''
      }`}
      onClick={handleCardClick}
    >
      {/* Cover image container */}
      <div className="relative">
        <div className="relative h-48">
          <BrandImage
            src={brand.coverImage}
            alt={`${brand.name} cover`}
            type="cover"
            className="w-full h-full object-cover object-left-top"
            priority={priority}
            fetchPriority={isLCPCandidate ? 'high' : priority ? 'high' : 'auto'}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
            <h3 className="text-lg font-bold text-white leading-tight">{brand.name}</h3>
            <div className="mt-1">
              <StarRating rating={brand.rating} />
            </div>
          </div>
        </div>
        
        {/* Logo with white border */}
        <div className="absolute -bottom-8 right-4 z-20">
          <div className="rounded-full bg-white shadow-md w-16 h-16 flex items-center justify-center overflow-hidden border-2 border-white">
            <BrandImage
              src={brand.logoImage}
              alt={`${brand.name} logo`}
              type="logo"
              className="w-full h-full object-contain"
              priority={priority}
            />
          </div>
        </div>
      </div>
      
      {/* Badges area - positioned below cover */}
      <div className="px-4 pt-3 pb-0">
        <div className="flex space-x-2 mb-2">
          {brand.verified ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              Verified
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
              </svg>
              Unverified
            </span>
          )}
          {brand.acceptsCrypto && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12zm1-8.25a1 1 0 10-2 0 1 1 0 002 0zM10 10a1 1 0 00-1 1v1a1 1 0 002 0v-1a1 1 0 00-1-1z"></path>
              </svg>
              Crypto
            </span>
          )}
        </div>
      </div>
      
      {/* Content area */}
      <div className="p-4 pt-2 flex-grow flex flex-col">
        {/* Only show description paragraph if it has content */}
        {brand.description && brand.description.trim() !== "" && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-4 overflow-hidden">{brand.description}</p>
        )}
        
        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-md mt-auto">
          <div className="flex items-center">
            <Gift className="text-blue-600 w-5 h-5 mr-2" />
            <div>
              <p className="text-xs font-medium text-gray-600">Special Bonus:</p>
              <p className="font-bold text-blue-600">Welcome Bonus: {brand.welcomeBonus || "100%"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandCard;